<template>
  <div class="entity-list-page rag-admin-dashboard">
    <header class="list-header">
      <div>
        <router-link
          v-if="fromProjectId"
          :to="`/projects/${fromProjectId}/rag-analysis`"
          class="back-link"
        >← RAG analysis</router-link>
        <router-link v-else to="/projects" class="back-link">← Projects</router-link>
        <h1>RAG admin</h1>
        <p class="list-sub">
          Four-aspect RAG performance — retrieval, classification, requirements, and evidence quality.
        </p>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-ghost" :disabled="loading" @click="refreshData">
          <i class="fas fa-sync-alt" :class="{ spinning: loading }" aria-hidden="true"></i>
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </header>

    <div class="toolbar controls-section">
      <div class="date-filters">
        <div class="filter-group">
          <label for="rag-admin-from">From</label>
          <input
            id="rag-admin-from"
            type="date"
            v-model="dateFilters.dateFrom"
            class="date-input"
            @change="loadDashboardData"
          />
        </div>
        <div class="filter-group">
          <label for="rag-admin-to">To</label>
          <input
            id="rag-admin-to"
            type="date"
            v-model="dateFilters.dateTo"
            class="date-input"
            @change="loadDashboardData"
          />
        </div>
        <label class="checkbox-label">
          <input
            type="checkbox"
            v-model="dateFilters.excludeExpired"
            @change="loadDashboardData"
          />
          Exclude expired documents
        </label>
      </div>
      <p class="last-updated">Last updated: {{ lastUpdated }}</p>
    </div>

    <!-- Overview Metrics -->
    <div class="overview-section">
        <div class="metrics-grid">
          <div class="metric-card total">
            <div class="metric-icon"><i class="fas fa-chart-bar"></i></div>
            <div class="metric-content">
              <div class="metric-value">{{ overallMetrics.totalAnalyses || 0 }}</div>
              <div class="metric-label">Total RAG Analyses</div>
            </div>
          </div>
          <div class="metric-card success">
            <div class="metric-icon"><i class="fas fa-check-circle"></i></div>
            <div class="metric-content">
              <div class="metric-value">{{ overallMetrics.successRate ? overallMetrics.successRate + '%' : 'N/A' }}</div>
              <div class="metric-label">Overall Success Rate</div>
            </div>
          </div>
          <div class="metric-card performance">
            <div class="metric-icon"><i class="fas fa-tachometer-alt"></i></div>
            <div class="metric-content">
              <div class="metric-value">{{ overallMetrics.avgProcessingTime ? overallMetrics.avgProcessingTime + 's' : 'N/A' }}</div>
              <div class="metric-label">Avg Processing Time</div>
            </div>
          </div>
          <div class="metric-card cost">
            <div class="metric-icon"><i class="fas fa-dollar-sign"></i></div>
            <div class="metric-content">
              <div class="metric-value">{{ overallMetrics.totalCost ? '$' + overallMetrics.totalCost : 'N/A' }}</div>
              <div class="metric-label">Total LLM Costs</div>
            </div>
          </div>
        </div>
    </div>

    <!-- Four Aspects Analysis -->
    <div class="aspects-section">
        <h2 class="section-title">Four-aspect performance</h2>
        
        <!-- Aspect 1: Knowledge Base Search & Pattern Recognition -->
        <div class="aspect-card">
          <div class="aspect-header">
            <div class="aspect-icon knowledge-base">
              <i class="fas fa-database"></i>
            </div>
            <div class="aspect-title">
              <h3>1. Knowledge Base Search & Pattern Recognition</h3>
              <p>How effectively we retrieve relevant knowledge and identify patterns</p>
            </div>
            <div class="aspect-score" :class="getScoreClass(aspects.knowledgeBase.score)">
              {{ aspects.knowledgeBase.score ? aspects.knowledgeBase.score + '%' : 'N/A' }}
            </div>
          </div>
          
          <div class="aspect-content">
            <div class="metrics-row">
              <div class="aspect-metric">
                <div class="metric-title">Knowledge Hits</div>
                <div class="metric-number">{{ aspects.knowledgeBase.knowledgeHits || 'N/A' }}</div>
                <div class="metric-subtitle">{{ aspects.knowledgeBase.knowledgeHitRate ? aspects.knowledgeBase.knowledgeHitRate + '% hit rate' : 'Hit rate not tracked' }}</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Pattern Matches</div>
                <div class="metric-number">{{ aspects.knowledgeBase.patternMatches || 'N/A' }}</div>
                <div class="metric-subtitle">{{ aspects.knowledgeBase.uniquePatterns || 0 }} unique patterns</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Relevance Score</div>
                <div class="metric-number">{{ aspects.knowledgeBase.avgRelevance ? aspects.knowledgeBase.avgRelevance + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Average relevance</div>
              </div>
            </div>
            
            <div class="patterns-section">
              <h4>Top Knowledge Patterns</h4>
              <div class="pattern-grid" v-if="aspects.knowledgeBase.topPatterns && aspects.knowledgeBase.topPatterns.length > 0">
                <div v-for="pattern in aspects.knowledgeBase.topPatterns" :key="pattern.id" class="pattern-item">
                  <div class="pattern-name">{{ pattern.name }}</div>
                  <div class="pattern-stats">
                    <span class="pattern-hits">{{ pattern.hits || 'N/A' }} hits</span>
                    <span class="pattern-confidence">{{ pattern.confidence ? pattern.confidence + '% confidence' : 'Confidence not tracked' }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="no-data-message">
                <i class="fas fa-info-circle"></i>
                <p>No knowledge patterns available yet. Add patterns to improve analysis accuracy.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Aspect 2: Document Classification & Context Extraction -->
        <div class="aspect-card">
          <div class="aspect-header">
            <div class="aspect-icon classification">
              <i class="fas fa-file-search"></i>
            </div>
            <div class="aspect-title">
              <h3>2. Document Classification & Context Extraction</h3>
              <p>How accurately we understand document types and extract context</p>
            </div>
            <div class="aspect-score" :class="getScoreClass(aspects.classification.score)">
              {{ aspects.classification.score ? aspects.classification.score + '%' : 'N/A' }}
            </div>
          </div>
          
          <div class="aspect-content">
            <div class="metrics-row">
              <div class="aspect-metric">
                <div class="metric-title">Classification Accuracy</div>
                <div class="metric-number">{{ aspects.classification.accuracy ? aspects.classification.accuracy + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Document type accuracy</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Context Quality</div>
                <div class="metric-number">{{ aspects.classification.contextQuality ? aspects.classification.contextQuality + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Avg context completeness</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Processing Time</div>
                <div class="metric-number">{{ aspects.classification.avgTime ? aspects.classification.avgTime + 's' : 'N/A' }}</div>
                <div class="metric-subtitle">Avg extraction time</div>
              </div>
            </div>
            
            <div class="classification-breakdown">
              <h4>Document Type Distribution</h4>
              <div class="doc-type-grid" v-if="aspects.classification.documentTypes && aspects.classification.documentTypes.length > 0">
                <div v-for="docType in aspects.classification.documentTypes" :key="docType.type" class="doc-type-item">
                  <div class="doc-type-name">{{ docType.type }}</div>
                  <div class="doc-type-bar">
                    <div class="doc-type-fill" :style="{ width: docType.percentage + '%' }"></div>
                  </div>
                  <div class="doc-type-stats">{{ docType.count }} docs ({{ docType.percentage }}%)</div>
                </div>
              </div>
              <div v-else class="no-data-message">
                <i class="fas fa-info-circle"></i>
                <p>No documents analyzed yet. Upload documents to see type distribution.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Aspect 3: Requirements Analysis & Validation -->
        <div class="aspect-card">
          <div class="aspect-header">
            <div class="aspect-icon requirements">
              <i class="fas fa-clipboard-check"></i>
            </div>
            <div class="aspect-title">
              <h3>3. Requirements Analysis & Validation</h3>
              <p>How effectively we evaluate compliance against requirements</p>
            </div>
            <div class="aspect-score" :class="getScoreClass(aspects.requirements.score)">
              {{ aspects.requirements.score ? aspects.requirements.score + '%' : 'N/A' }}
            </div>
          </div>
          
          <div class="aspect-content">
            <div class="metrics-row">
              <div class="aspect-metric">
                <div class="metric-title">Requirements Processed</div>
                <div class="metric-number">{{ aspects.requirements.totalProcessed || 0 }}</div>
                <div class="metric-subtitle">{{ aspects.requirements.uniqueRequirements || 0 }} unique types</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Pass Rate</div>
                <div class="metric-number">{{ aspects.requirements.passRate ? aspects.requirements.passRate + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Overall compliance rate</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Confidence Score</div>
                <div class="metric-number">{{ aspects.requirements.avgConfidence ? aspects.requirements.avgConfidence + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Average LLM confidence</div>
              </div>
            </div>
            
            <div class="requirements-breakdown">
              <h4>Requirements Performance by Type</h4>
              <div class="req-performance-grid" v-if="aspects.requirements.topRequirements && aspects.requirements.topRequirements.length > 0">
                <div v-for="req in aspects.requirements.topRequirements" :key="req.code" class="req-performance-item">
                  <div class="req-name">{{ req.name }}</div>
                  <div class="req-stats">
                    <div class="req-pass-rate" :class="getScoreClass(req.passRate)">{{ req.passRate }}% pass</div>
                    <div class="req-volume">{{ req.totalChecks }} checks</div>
                  </div>
                </div>
              </div>
              <div v-else class="no-data-message">
                <i class="fas fa-info-circle"></i>
                <p>No requirement analysis data yet. Run compliance checks to see performance metrics.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Aspect 4: Evidence Retrieval & Quality Assurance -->
        <div class="aspect-card">
          <div class="aspect-header">
            <div class="aspect-icon evidence">
              <i class="fas fa-search-plus"></i>
            </div>
            <div class="aspect-title">
              <h3>4. Evidence Retrieval & Quality Assurance</h3>
              <p>How reliably we extract and validate evidence for decisions</p>
            </div>
            <div class="aspect-score" :class="getScoreClass(aspects.evidence.score)">
              {{ aspects.evidence.score ? aspects.evidence.score + '%' : 'N/A' }}
            </div>
          </div>
          
          <div class="aspect-content">
            <div class="metrics-row">
              <div class="aspect-metric">
                <div class="metric-title">Evidence Quality</div>
                <div class="metric-number">{{ aspects.evidence.qualityScore ? aspects.evidence.qualityScore + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Avg evidence completeness</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Validation Accuracy</div>
                <div class="metric-number">{{ aspects.evidence.validationAccuracy ? aspects.evidence.validationAccuracy + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Cross-validation success</div>
              </div>
              <div class="aspect-metric">
                <div class="metric-title">Evidence Coverage</div>
                <div class="metric-number">{{ aspects.evidence.coverageRate ? aspects.evidence.coverageRate + '%' : 'N/A' }}</div>
                <div class="metric-subtitle">Requirements with evidence</div>
              </div>
            </div>
            
            <div class="evidence-quality-breakdown">
              <h4>Evidence Quality Distribution</h4>
              <div class="quality-chart" v-if="aspects.evidence.qualityDistribution && aspects.evidence.qualityDistribution.length > 0">
                <div v-for="quality in aspects.evidence.qualityDistribution" :key="quality.range" class="quality-item">
                  <div class="quality-label">{{ quality.range }}</div>
                  <div class="quality-bar">
                    <div class="quality-fill" :style="{ width: quality.percentage + '%', backgroundColor: quality.color }"></div>
                  </div>
                  <div class="quality-count">{{ quality.count }} analyses</div>
                </div>
              </div>
              <div v-else class="no-data-message">
                <i class="fas fa-info-circle"></i>
                <p>No evidence quality data yet. Complete more analyses to see quality distribution.</p>
              </div>
            </div>
          </div>
        </div>
    </div>

    <!-- Trends and Insights -->
    <div class="trends-section">
        <h2 class="section-title">Performance trends &amp; insights</h2>
        
        <div class="trends-grid">
          <div class="trend-card">
            <h4>Weekly Performance Trend</h4>
            <div class="trend-chart" v-if="weeklyTrend && weeklyTrend.length > 0">
              <div v-for="(day, index) in weeklyTrend" :key="index" class="trend-day">
                <div class="trend-bar" :style="{ height: day.value + '%' }"></div>
                <div class="trend-label">{{ day.day }}</div>
              </div>
            </div>
            <div v-else class="no-data-message">
              <i class="fas fa-info-circle"></i>
              <p>Performance trend data not available yet. Daily metrics tracking needed.</p>
            </div>
          </div>
          
          <div class="insights-card">
            <h4>Key Insights</h4>
            <div class="insights-list" v-if="keyInsights && keyInsights.length > 0">
              <div v-for="insight in keyInsights" :key="insight.id" class="insight-item" :class="insight.type">
                <i :class="insight.icon"></i>
                <span>{{ insight.message }}</span>
              </div>
            </div>
            <div v-else class="no-data-message">
              <i class="fas fa-info-circle"></i>
              <p>No insights available yet. Run analyses to generate insights.</p>
            </div>
          </div>
          
          <div class="alerts-card">
            <h4>System Alerts</h4>
            <div class="alerts-list" v-if="systemAlerts && systemAlerts.length > 0">
              <div v-for="alert in systemAlerts" :key="alert.id" class="alert-item" :class="alert.severity">
                <i :class="alert.icon"></i>
                <div class="alert-content">
                  <div class="alert-message">{{ alert.message }}</div>
                  <div class="alert-time">{{ formatTime(alert.timestamp) }}</div>
                </div>
              </div>
            </div>
            <div v-else class="no-data-message">
              <i class="fas fa-info-circle"></i>
              <p>No system alerts at this time.</p>
            </div>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'RAGAdminDashboard',
  computed: {
    fromProjectId() {
      return this.$route.query.fromProject || null;
    },
  },
  data() {
    return {
      loading: false,
      lastUpdated: new Date().toLocaleString(),
      
      // Date filtering controls
      dateFilters: {
        dateFrom: null,
        dateTo: null,
        excludeExpired: true
      },
      
      // Overall metrics across all aspects - will be populated from API
      overallMetrics: {
        totalAnalyses: 0,
        successRate: null,
        avgProcessingTime: null,
        totalCost: null
      },
      
      // Four aspects detailed metrics - will be populated from API
      aspects: {
        knowledgeBase: {
          score: null,
          knowledgeHits: null,
          knowledgeHitRate: null,
          patternMatches: null,
          uniquePatterns: 0,
          avgRelevance: null,
          topPatterns: []
        },
        
        classification: {
          score: null,
          accuracy: null,
          contextQuality: null,
          avgTime: null,
          documentTypes: []
        },
        
        requirements: {
          score: null,
          totalProcessed: 0,
          uniqueRequirements: 0,
          passRate: null,
          avgConfidence: null,
          topRequirements: []
        },
        
        evidence: {
          score: null,
          qualityScore: null,
          validationAccuracy: null,
          coverageRate: null,
          qualityDistribution: []
        }
      },
      
      // Weekly performance trend - will be populated from API
      weeklyTrend: [],
      
      // Key insights - will be populated from API
      keyInsights: [],
      
      // System alerts - will be populated from API
      systemAlerts: []
    }
  },
  
  mounted() {
    this.loadDashboardData();
  },
  
  methods: {
    async loadDashboardData() {
      this.loading = true;
      try {
        console.log('Loading RAG admin metrics from API...');
        
        // Build query parameters for date filtering
        const params = {};
        if (this.dateFilters.dateFrom) {
          params.date_from = this.dateFilters.dateFrom;
        }
        if (this.dateFilters.dateTo) {
          params.date_to = this.dateFilters.dateTo;
        }
        params.exclude_expired = this.dateFilters.excludeExpired;
        
        const response = await api.get('/rag/admin/metrics/', { params });
        
        if (response.data) {
          // Update metrics with real data from backend
          this.overallMetrics = response.data.overall || this.overallMetrics;
          this.aspects = response.data.aspects || this.aspects;
          this.weeklyTrend = response.data.weeklyTrend || this.weeklyTrend;
          this.keyInsights = response.data.keyInsights || this.keyInsights;
          this.systemAlerts = response.data.systemAlerts || this.systemAlerts;
          
          console.log('RAG Admin Dashboard data loaded successfully');
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Keep using mock data if API fails
        console.log('Using mock data as fallback');
      } finally {
        this.loading = false;
        this.lastUpdated = new Date().toLocaleString();
      }
    },
    
    async refreshData() {
      await this.loadDashboardData();
    },
    
    getScoreClass(score) {
      if (score === null || score === undefined) return 'not-available';
      if (score >= 90) return 'excellent';
      if (score >= 80) return 'good';
      if (score >= 70) return 'average';
      return 'poor';
    },
    
    formatTime(timestamp) {
      const now = new Date();
      const diff = now - timestamp;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ago`;
      }
      return `${minutes}m ago`;
    }
  }
}
</script>

<style scoped>
.rag-admin-dashboard {
  /* Page chrome: global .entity-list-page */
  padding-bottom: 2.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.controls-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}

.date-filters {
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
}

.date-input {
  padding: 0.45rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.45rem;
  font-size: 0.875rem;
  background: #fff;
  color: #0f172a;
}

.date-input:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  box-shadow: 0 0 0 3px rgba(30, 58, 95, 0.12);
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #334155;
  padding-bottom: 0.35rem;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.last-updated {
  color: #64748b;
  font-size: 0.85rem;
  margin: 0;
}

/* Overview Metrics */
.overview-section {
  margin-bottom: 1.5rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.85rem;
}

.metric-card {
  background: #fff;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.metric-icon {
  font-size: 1.25rem;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.55rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.metric-card.total .metric-icon { background: #eff6ff; color: #2563eb; }
.metric-card.success .metric-icon { background: #ecfdf5; color: #059669; }
.metric-card.performance .metric-icon { background: #fffbeb; color: #d97706; }
.metric-card.cost .metric-icon { background: #fef2f2; color: #dc2626; }

.metric-content {
  flex: 1;
  min-width: 0;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.15rem;
  line-height: 1.2;
}

.metric-label {
  color: #64748b;
  font-size: 0.8rem;
}

/* Aspects Section */
.aspects-section {
  padding: 0;
  background: transparent;
  min-height: 0;
}

.section-title {
  text-align: left;
  font-size: 1.15rem;
  color: #0f172a;
  margin: 0 0 1rem;
  font-weight: 650;
}

.aspect-card {
  background: white;
  border-radius: 10px;
  box-shadow: none;
  margin-bottom: 1rem;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.aspect-header {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.aspect-icon {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
}

.aspect-icon.knowledge-base { background: linear-gradient(135deg, #3B82F6, #1E40AF); }
.aspect-icon.classification { background: linear-gradient(135deg, #10B981, #047857); }
.aspect-icon.requirements { background: linear-gradient(135deg, #F59E0B, #D97706); }
.aspect-icon.evidence { background: linear-gradient(135deg, #8B5CF6, #7C3AED); }

.aspect-title {
  flex: 1;
}

.aspect-title h3 {
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.aspect-title p {
  color: #6b7280;
  margin: 0;
}

.aspect-score {
  font-size: 2.5rem;
  font-weight: bold;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  color: white;
}

.aspect-score.excellent { background: #10B981; }
.aspect-score.good { background: #3B82F6; }
.aspect-score.average { background: #F59E0B; }
.aspect-score.poor { background: #EF4444; }
.aspect-score.not-available { background: #6B7280; }

.aspect-content {
  padding: 2rem;
}

.metrics-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.aspect-metric {
  text-align: center;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.metric-title {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.metric-number {
  font-size: 2rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.metric-subtitle {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* Pattern Grids */
.patterns-section, .classification-breakdown, .requirements-breakdown, .evidence-quality-breakdown {
  margin-top: 2rem;
}

.patterns-section h4, .classification-breakdown h4, .requirements-breakdown h4, .evidence-quality-breakdown h4 {
  color: #1f2937;
  margin-bottom: 1rem;
  font-weight: 600;
}

.pattern-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.pattern-item {
  background: #f3f4f6;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
}

.pattern-name {
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.pattern-stats {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
}

.pattern-hits {
  color: #3B82F6;
  font-weight: 500;
}

.pattern-confidence {
  color: #10B981;
  font-weight: 500;
}

/* Document Type Grid */
.doc-type-grid {
  display: grid;
  gap: 1rem;
}

.doc-type-item {
  display: grid;
  grid-template-columns: 200px 1fr 150px;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
}

.doc-type-name {
  font-weight: 500;
  color: #1f2937;
}

.doc-type-bar {
  background: #e5e7eb;
  border-radius: 6px;
  height: 8px;
  overflow: hidden;
}

.doc-type-fill {
  background: linear-gradient(135deg, #3B82F6, #1E40AF);
  height: 100%;
  transition: width 0.5s ease;
}

.doc-type-stats {
  font-size: 0.85rem;
  color: #6b7280;
  text-align: right;
}

/* Requirements Performance */
.req-performance-grid {
  display: grid;
  gap: 1rem;
}

.req-performance-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.req-name {
  font-weight: 500;
  color: #1f2937;
}

.req-stats {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.req-pass-rate {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
}

.req-volume {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Quality Chart */
.quality-chart {
  display: grid;
  gap: 0.75rem;
}

.quality-item {
  display: grid;
  grid-template-columns: 100px 1fr 120px;
  align-items: center;
  gap: 1rem;
}

.quality-label {
  font-weight: 500;
  color: #1f2937;
  font-size: 0.9rem;
}

.quality-bar {
  background: #e5e7eb;
  border-radius: 6px;
  height: 8px;
  overflow: hidden;
}

.quality-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.quality-count {
  font-size: 0.85rem;
  color: #6b7280;
  text-align: right;
}

/* Trends Section */
.trends-section {
  padding: 0;
  margin-top: 1.5rem;
  background: transparent;
}

.trends-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.trend-card, .insights-card, .alerts-card {
  background: white;
  border-radius: 10px;
  padding: 1.15rem;
  box-shadow: none;
  border: 1px solid #e2e8f0;
}

.trend-card h4, .insights-card h4, .alerts-card h4 {
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.trend-chart {
  display: flex;
  justify-content: space-between;
  align-items: end;
  height: 150px;
  padding: 1rem 0;
}

.trend-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.trend-bar {
  width: 30px;
  background: linear-gradient(135deg, #3B82F6, #1E40AF);
  border-radius: 4px 4px 0 0;
  min-height: 20px;
  transition: height 0.5s ease;
}

.trend-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
}

/* Insights List */
.insights-list {
  display: grid;
  gap: 1rem;
}

.insight-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
}

.insight-item.success { background: #ecfdf5; color: #047857; }
.insight-item.warning { background: #fffbeb; color: #d97706; }
.insight-item.info { background: #eff6ff; color: #1d4ed8; }

.insight-item i {
  font-size: 1rem;
}

/* Alerts List */
.alerts-list {
  display: grid;
  gap: 1rem;
}

.alert-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.alert-item.warning { background: #fffbeb; border-color: #f59e0b; }
.alert-item.info { background: #eff6ff; border-color: #3b82f6; }
.alert-item.success { background: #ecfdf5; border-color: #10b981; }

.alert-content {
  flex: 1;
}

.alert-message {
  color: #1f2937;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.alert-time {
  color: #6b7280;
  font-size: 0.8rem;
}

/* No Data Messages */
.no-data-message {
  text-align: center;
  padding: 2rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 2px dashed #d1d5db;
  color: #6b7280;
}

.no-data-message i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #9ca3af;
}

.no-data-message p {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .aspect-header {
    flex-direction: column;
    text-align: center;
  }
  
  .metrics-row {
    grid-template-columns: 1fr;
  }
  
  .doc-type-item {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .req-performance-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .trends-grid {
    grid-template-columns: 1fr;
  }
}
</style>